import React from 'react';
import { toast } from 'sonner';
import { oeffneMeldung } from '../lib/testing/context';

interface Props {
  children: React.ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

/**
 * Fängt Render-Fehler einer Unterseite ab, damit ein einzelner Crash nicht die
 * komplette App in einen White-Screen reißt. Zeigt eine Fallback-Ansicht + Toast.
 * Wird pro Route gekeyed → beim Navigieren automatisch zurückgesetzt.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info?.componentStack ?? null });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
    try {
      toast.error('Es ist ein Fehler aufgetreten. Diese Ansicht konnte nicht vollständig geladen werden.');
    } catch {
      /* toast darf den Fallback nie verhindern */
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  // Nur Beta-Testphase: Das sind die Fehler, die sonst nie gemeldet werden — der
  // Stacktrace liegt hier vor, der Melder müsste ihn sonst abtippen.
  private handleReport = () => {
    oeffneMeldung({
      titel: this.state.error?.message?.slice(0, 120),
      boundary: {
        message: this.state.error?.message ?? 'Unbekannter Fehler',
        stack: this.state.error?.stack ?? undefined,
        componentStack: this.state.componentStack ?? undefined,
      },
    });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="text-lg font-semibold">
          {this.props.fallbackTitle ?? 'Etwas ist schiefgelaufen'}
        </div>
        <p className="max-w-md text-sm text-muted-foreground">
          Beim Anzeigen dieser Ansicht ist ein Fehler aufgetreten. Du kannst es erneut
          versuchen oder zu einer anderen Seite wechseln.
        </p>
        {this.state.error?.message ? (
          <p className="max-w-md break-words text-xs text-muted-foreground/70">
            {this.state.error.message}
          </p>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Erneut versuchen
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Neu laden
          </button>
          <button
            type="button"
            onClick={this.handleReport}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Diesen Fehler melden
          </button>
        </div>
      </div>
    );
  }
}
